<script setup lang="ts">
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';
import GaiaSwitch from '../base/GaiaSwitch.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  /** The line under the label — say what turning it on actually does. */
  hint: {
    type: String,
    default: undefined,
  },
  checked: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /** plain = a row in a list of options; card = its own bordered surface. */
  card: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ 'update:checked': [checked: boolean] }>();

const border = gaiaNeutral.borderCard;
const borderHover = gaiaNeutral.borderHover;
const soft = gaiaNeutral.surfaceSoft;
const surface = gaiaNeutral.surface;
</script>

<template>
  <!-- A setting with its explanation and a switch: the playbook wizard's
       generation options, the view's "visible to others". Clicking anywhere on
       the row flips it, so the whole row is the target rather than the switch
       alone. -->
  <div
    class="gaia-toggle-row"
    :class="{ card: props.card, disabled: props.disabled }"
    @click="!props.disabled && emit('update:checked', !props.checked)"
  >
    <n-flex vertical :size="2" :style="{ minWidth: 0 }">
      <gaia-text size="body" weight="semibold" tone="ink">{{ props.label }}</gaia-text>
      <gaia-text v-if="props.hint" size="control" tone="subtle" :style="{ lineHeight: 1.5 }">
        {{ props.hint }}
      </gaia-text>
    </n-flex>
    <!-- `.stop` because the row is also a click target: naive's switch does not
         stop propagation, so without it one click on the switch emits twice —
         once from the switch, once from the row negating a prop that has not
         re-rendered yet. -->
    <gaia-switch
      :value="props.checked"
      :disabled="props.disabled"
      :style="{ marginLeft: 'auto', flex: 'none' }"
      @click.stop
      @update:value="(value: boolean) => emit('update:checked', value)"
    />
  </div>
</template>

<style scoped>
.gaia-toggle-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 11px 13px;
  border: 1px solid v-bind(border);
  border-radius: 10px;
  background: v-bind(surface);
  cursor: pointer;
}

.gaia-toggle-row:hover {
  border-color: v-bind(borderHover);
}

/* The card form sits on the soft surface and reads as its own block rather
   than one of several stacked options. */
.gaia-toggle-row.card {
  padding: 14px 16px;
  background: v-bind(soft);
}

.gaia-toggle-row.disabled {
  cursor: default;
}

.gaia-toggle-row.disabled:hover {
  border-color: v-bind(border);
}
</style>
